import type { ReactNode } from "react";

/**
 * Cartão de foto: a foto inteira em cima, o nome embaixo — e todo cartão
 * com a MESMA altura.
 *
 * Referência: os cartões de receita do Dribbble 15125197 e 16053853 (foto,
 * nome, tempo). O acervo de fotos do app é quadrado com o prato no centro:
 * qualquer legenda por cima da foto tapava a comida. Então o texto mora
 * embaixo, numa área de altura FIXA — duas linhas de nome (corta com
 * reticências) e uma linha de apoio, ocupada mesmo quando vazia. É o que
 * impede a grade de virar escada quando um nome tem uma linha e o vizinho
 * duas.
 *
 * `largura` fixa é para trilho (rola de lado); sem ela o cartão ocupa a
 * célula da grade. `selo` é o ponto do semáforo, no canto da foto.
 */
export function CartaoFoto({
  nome,
  linha2,
  foto,
  capa,
  selo,
  onClick,
  largura,
  proporcao = "1 / 1",
}: {
  nome: string;
  linha2?: string;
  foto?: string;
  /** Nó próprio para a imagem (CachedImage, por exemplo). Tem precedência sobre `foto`. */
  capa?: ReactNode;
  /** Marca pequena no canto superior esquerdo da foto (semáforo). */
  selo?: ReactNode;
  onClick?: () => void;
  /** Largura fixa, para trilho. Sem ela, ocupa a célula da grade. */
  largura?: number;
  /** Proporção CSS da foto. O acervo é quadrado, então 1/1 por padrão. */
  proporcao?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="toque block shrink-0 overflow-hidden bg-card text-left"
      style={{
        width: largura ?? "100%",
        borderRadius: "var(--raio-cartao)",
        boxShadow: "inset 0 0 0 1px hsl(var(--border))",
        scrollSnapAlign: "start",
      }}
    >
      <span className="relative block w-full bg-muted" style={{ aspectRatio: proporcao }}>
        <span className="absolute inset-0">
          {capa ?? (foto ? <img src={foto} alt="" loading="lazy" className="h-full w-full object-cover" /> : null)}
        </span>
        {selo && <span className="absolute left-3 top-3">{selo}</span>}
      </span>

      <span className="block" style={{ padding: "12px 14px 14px" }}>
        <span
          className="block font-bold"
          style={{
            fontSize: "var(--t-linha)",
            lineHeight: 1.25,
            height: "calc(var(--t-linha) * 2.5)",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
          }}
        >
          {nome}
        </span>
        <span
          className="block truncate"
          style={{
            fontSize: "var(--t-apoio)",
            lineHeight: 1.4,
            height: "calc(var(--t-apoio) * 1.4)",
            marginTop: 4,
            color: "var(--tinta-apoio)",
          }}
        >
          {linha2 ?? " "}
        </span>
      </span>
    </button>
  );
}
